/**
 * Stripe Utility Functions
 * Handles Stripe checkout integration
 */

import { loadStripe } from '@stripe/stripe-js';
import { agentAuthHeaders } from './functionAuth';
import { SOLO_HOMEOWNER_LIMIT, HOMEOWNERS_PER_SEAT } from '../shared/planLimits';
import { canShowPaymentUI, getApiUrl, NATIVE_PLAN_NOTICE } from './platform';
import { browserAppBase } from '../shared/siteUrl';
import { trialDisplayTimeZone } from '../shared/trialStatus';
import { devLog } from './devLog';

function assertWebBilling() {
  if (!canShowPaymentUI()) {
    throw new Error(NATIVE_PLAN_NOTICE);
  }
}

// Initialize Stripe
const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
devLog('Stripe publishable key loaded:', publishableKey ? 'Yes' : 'No');

const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

// Price IDs from environment variables - Flexible Team Seating
export const STRIPE_PRICES = {
  SOLO: process.env.REACT_APP_STRIPE_SOLO_PRICE,
  TEAM_2: process.env.REACT_APP_STRIPE_TEAM_2_PRICE,
  TEAM_3: process.env.REACT_APP_STRIPE_TEAM_3_PRICE,
  TEAM_4: process.env.REACT_APP_STRIPE_TEAM_4_PRICE,
  TEAM_5: process.env.REACT_APP_STRIPE_TEAM_5_PRICE,
  TEAM_6: process.env.REACT_APP_STRIPE_TEAM_6_PRICE,
  TEAM_7: process.env.REACT_APP_STRIPE_TEAM_7_PRICE,
  TEAM_8: process.env.REACT_APP_STRIPE_TEAM_8_PRICE,
  TEAM_9: process.env.REACT_APP_STRIPE_TEAM_9_PRICE,
  TEAM_10: process.env.REACT_APP_STRIPE_TEAM_10_PRICE
};

// Team pricing structure
export const TEAM_PRICING = [
  { agents: 2, price: 98, priceId: STRIPE_PRICES.TEAM_2, homeowners: 400 },
  { agents: 3, price: 133, priceId: STRIPE_PRICES.TEAM_3, homeowners: 600 },
  { agents: 4, price: 168, priceId: STRIPE_PRICES.TEAM_4, homeowners: 800 },
  { agents: 5, price: 203, priceId: STRIPE_PRICES.TEAM_5, homeowners: 1000 },
  { agents: 6, price: 238, priceId: STRIPE_PRICES.TEAM_6, homeowners: 1200 },
  { agents: 7, price: 273, priceId: STRIPE_PRICES.TEAM_7, homeowners: 1400 },
  { agents: 8, price: 308, priceId: STRIPE_PRICES.TEAM_8, homeowners: 1600 },
  { agents: 9, price: 343, priceId: STRIPE_PRICES.TEAM_9, homeowners: 1800 },
  { agents: 10, price: 378, priceId: STRIPE_PRICES.TEAM_10, homeowners: 2000 }
];

devLog('Stripe prices loaded');

/**
 * Create a checkout session and redirect to Stripe
 */
export async function createCheckoutSession({ priceId, agentId, email, consent }) {
  assertWebBilling();
  try {
    devLog('Creating checkout session');

    // Call Netlify function to create checkout session
    const response = await fetch(getApiUrl('/.netlify/functions/create-checkout-session'), {
      method: 'POST',
      headers: await agentAuthHeaders(),
      body: JSON.stringify({
        priceId,
        agentId,
        email,
        successUrl: `${browserAppBase(window.location)}?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${browserAppBase(window.location)}?checkout=canceled`,
        consent: consent || null,
        timeZone: trialDisplayTimeZone(),
      })
    });

    devLog('Checkout response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Checkout error response:', errorText);
      let error;
      try {
        error = JSON.parse(errorText);
      } catch {
        error = { error: errorText || 'Failed to create checkout session' };
      }
      throw new Error(error.error || 'Failed to create checkout session');
    }

    const { sessionId, url } = await response.json();
    devLog('Checkout session created');

    // Redirect to Stripe Checkout
    if (url) {
      window.location.href = url;
    } else if (sessionId && stripePromise) {
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        throw error;
      }
    } else {
      throw new Error('No checkout URL or session ID returned');
    }
  } catch (error) {
    console.error('Checkout error:', error);
    throw error;
  }
}

/**
 * Get team size from price ID
 */
export function getTeamSizeFromPriceId(priceId) {
  const teamPlan = TEAM_PRICING.find(t => t.priceId === priceId);
  return teamPlan ? teamPlan.agents : null;
}

/**
 * Get plan name from price ID
 */
export function getPlanFromPriceId(priceId) {
  if (priceId === STRIPE_PRICES.SOLO) return 'solo';
  const teamSize = getTeamSizeFromPriceId(priceId);
  if (teamSize) return 'team';
  return 'unknown';
}

/**
 * Get plan details by name or team size
 */
export function getPlanDetails(planName, teamSize = null) {
  if (planName === 'solo') {
    return {
      name: 'Solo Agent',
      price: 49,
      priceId: STRIPE_PRICES.SOLO,
      clientLimit: SOLO_HOMEOWNER_LIMIT,
      agents: 1,
      features: [
        '1 agent account',
        `${SOLO_HOMEOWNER_LIMIT} homeowners`,
        '24/7 AI home assistant',
        'Automated maintenance tracking',
        'Document storage',
        'Vendor directory',
        'Email notifications',
        'Project checklists',
        'Mobile dashboards'
      ]
    };
  }

  if (planName === 'team' && teamSize) {
    const teamPlan = TEAM_PRICING.find(t => t.agents === teamSize);
    if (teamPlan) {
      return {
        name: `${teamSize}-Agent Team`,
        price: teamPlan.price,
        priceId: teamPlan.priceId,
        clientLimit: teamPlan.homeowners,
        agents: teamSize,
        features: [
          `${teamSize} agent accounts`,
          `${teamPlan.homeowners} homeowners, shared by the team (${HOMEOWNERS_PER_SEAT} per seat)`,
          'Team collaboration',
          '24/7 AI home assistant',
          'Automated maintenance tracking',
          'Document storage',
          'Vendor directory',
          'Email notifications',
          'Project checklists',
          'Mobile dashboards'
        ]
      };
    }
  }

  if (planName === 'enterprise') {
    return {
      name: 'Enterprise',
      price: 'Custom',
      priceId: null,
      clientLimit: 'Unlimited',
      agents: '10+',
      features: [
        '10+ agent accounts',
        'Custom homeowner limit',
        'All features included',
        'Dedicated account manager',
        'Priority support'
      ]
    };
  }

  // Default to Solo
  return getPlanDetails('solo');
}

/** Confirm the Checkout return and let the function write the trial. */
export async function verifyCheckoutSession(sessionId) {
  const response = await fetch(getApiUrl('/.netlify/functions/verify-checkout-session'), {
    method: 'POST',
    headers: await agentAuthHeaders(),
    body: JSON.stringify({ sessionId }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Could not verify checkout');
  }
  return data;
}

/**
 * Create customer portal session
 */
export async function createPortalSession(customerId) {
  assertWebBilling();
  try {
    const response = await fetch(getApiUrl('/.netlify/functions/create-portal-session'), {
      method: 'POST',
      headers: await agentAuthHeaders(),
      body: JSON.stringify({})
    });

    if (!response.ok) {
      throw new Error('Failed to create portal session');
    }

    const { url } = await response.json();
    window.location.href = url;
  } catch (error) {
    console.error('Portal session error:', error);
    throw error;
  }
}

/**
 * Update subscription plan (upgrade/downgrade)
 */
/**
 * Plus or Max on the current subscription. addon is none, plus, or max.
 * The function prorates the second subscription item.
 */
export async function updateHomeownerAddon({ addon }) {
  assertWebBilling();
  const response = await fetch(getApiUrl('/.netlify/functions/update-homeowner-addon'), {
    method: 'POST',
    headers: await agentAuthHeaders(),
    body: JSON.stringify({ addon }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to update the homeowner add-on');
  }
  return data;
}

export async function updateSubscription({ customerId, newPriceId, agentId, confirmSolo, consent }) {
  assertWebBilling();
  try {
    devLog('Updating subscription');

    const payload = { newPriceId, timeZone: trialDisplayTimeZone() };
    if (confirmSolo === true) payload.confirmSolo = true;
    if (consent) payload.consent = consent;

    const response = await fetch(getApiUrl('/.netlify/functions/update-subscription'), {
      method: 'POST',
      headers: await agentAuthHeaders(),
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update subscription');
    }

    devLog('Subscription updated successfully');
    return data;
  } catch (error) {
    console.error('Subscription update error:', error);
    throw error;
  }
}
